const SESSION_KEY = 'user_session';

/**
 * Summary: 寫入 Session 至 sessionStorage。
 * Params: session {Session} 含 user_id、role、permission
 * Return: void
 * Remarks: 通常由 login() / checkAuth() 內部呼叫
 */
export function setSession(session) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

/**
 * Summary: 取得完整 Session 物件。
 * Params: 無
 * Return: Session | null
 * Remarks: storage key 為 user_session
 */
export function getSession() {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Summary: 清除 sessionStorage 中的 Session。
 * Params: 無
 * Return: void
 * Remarks: logout() 與 checkAuth 失敗時呼叫
 */
export function clearSession() {
  sessionStorage.removeItem(SESSION_KEY);
}

/**
 * Summary: 取得目前登入者 user_id。
 * Params: 無
 * Return: string | null
 * Remarks: 未登入回 null
 */
export function getUserId() {
  return getSession()?.user_id ?? null;
}

/**
 * Summary: 取得目前登入者角色物件。
 * Params: 無
 * Return: Role | null
 * Remarks: 含 role_name_zh / en / vi / th，依 i18n 語系選欄位顯示
 */
export function getRole() {
  return getSession()?.role ?? null;
}

/**
 * Summary: 取得目前登入者權限陣列。
 * Params: 無
 * Return: Permission[]
 * Remarks: 未登入回空陣列 []
 */
export function getPermission() {
  return getSession()?.permission ?? [];
}

/**
 * Summary: 檢查是否擁有指定 permission_id。
 * Params: permissionId {string} 對應 permission.permission_id
 * Return: boolean
 * Remarks: 用於按鈕/頁面權限控制，例 hasPermission('P005')
 */
export function hasPermission(permissionId) {
  return getPermission().some((item) => item.permission_id === permissionId);
}

/**
 * Summary: 取得目前登入者個人資料（user_information）。
 * Return: Profile | null
 */
export function getProfile() {
  return getSession()?.profile ?? null;
}

/**
 * Summary: 依語系取得顯示名稱。
 * Params: language {string} i18n 語系（zh-TW / en / th / vi）
 */
export function getDisplayName(language = 'zh-TW') {
  const profile = getProfile();
  const userId = getUserId();

  if (!profile) {
    return userId || '';
  }

  const langKey = language.split('-')[0];
  const nameByLang = {
    'zh-TW': profile.name_zh,
    zh: profile.name_zh,
    en: profile.name_en,
    th: profile.name_th,
    vi: profile.name_vi
  };

  return (
    nameByLang[language] ||
    nameByLang[langKey] ||
    profile.name_zh ||
    profile.name_en ||
    profile.name_vi ||
    profile.name_th ||
    userId ||
    ''
  );
}
